import { z } from "zod";
import { isoDateSchema as isoDate, reportFilterSchema } from "./report-filter.js";
/**
 * The Prompts table: every question on a report, with what its answers did.
 *
 * One payload rather than a column per endpoint. Each widget here answers a
 * question about the same set of captures, and six requests could return six
 * views of a moving set — a coverage of 100% beside a mention count taken
 * against a different denominator.
 *
 * **Every percentage arrives with its denominator.** `naming` travels with
 * `responses`, `ownCitations` with `totalCitations`. A screen that receives a
 * bare percentage cannot tell 1-of-1 from 40-of-40, and those are not the same
 * finding.
 */
/** The shared range-and-engine filter, named for the screen that uses it. */
export const promptTableFilterSchema = reportFilterSchema;
export const promptTableRowSchema = z.object({
    promptId: z.string(),
    text: z.string(),
    /** Answers captured for this question in range. Every denominator here. */
    responses: z.number().int().min(0),
    /**
     * Captures asked for and not yet answered.
     *
     * The difference between "nobody named you in 28 answers" and "we have not
     * asked yet", which are opposite states that a bare `responses: 0` renders
     * identically. A question subscribed a minute ago has `responses: 0` and
     * `awaiting: 2`, and the screen says so rather than showing it a row of
     * zeroes it would reasonably read as a result (§1).
     */
    awaiting: z.number().int().min(0),
    /**
     * Answers that have arrived and have not been read yet.
     *
     * A third state between "waiting for the engine" and "here is the finding".
     * The answer is stored; which companies it names and how it spoke about them
     * are read by a separate job a minute or more later. Without this number the
     * gap looked like a finished result of zero, which is the confusion §1 is
     * about: a question added a moment ago is not a question nobody was named in.
     */
    reading: z.number().int().min(0),
    /**
     * Answers we hold and could not read. The reading failed every attempt, so
     * the answer is on file and its findings never will be unless it is retried.
     * Shown rather than swallowed: silence here reads as "no companies named".
     */
    unreadable: z.number().int().min(0),
    /** Captures that finished without a usable answer. Never presented as pending. */
    failed: z.number().int().min(0),
    /** Answers that named the subject brand. */
    naming: z.number().int().min(0),
    /**
     * The subject's average place among the brands an answer names, over the
     * answers that named it. Null when none did: no place, never a zero.
     */
    averageRank: z.number().nullable(),
    /** Every time the subject was named, across those answers. */
    mentions: z.number().int().min(0),
    /** Every mention of a tracked brand in those answers. */
    totalMentions: z.number().int().min(0),
    /** Citations to a site the subject owns. */
    ownCitations: z.number().int().min(0),
    /** Every citation in those answers. */
    totalCitations: z.number().int().min(0),
    /**
     * Tone, counted rather than scored.
     *
     * Three numbers, so the screen can tell "balanced" from "nobody read it" —
     * a single net figure collapses 50 against 50 into the same 0 as an unscored
     * question, and those are opposite states.
     */
    positive: z.number().int().min(0),
    neutral: z.number().int().min(0),
    negative: z.number().int().min(0),
    /**
     * The report stopped tracking this question. Nothing new is captured for it,
     * and the answers it already has still count on this report.
     */
    paused: z.boolean(),
    /** Which competitors these answers named, most-mentioned first. */
    competitors: z.array(z.object({
        brandId: z.string(),
        brandName: z.string(),
        mentions: z.number().int().min(0),
        /** Answers that named it, counted as `naming` counts them for the subject. */
        naming: z.number().int().min(0),
    })),
    tags: z.array(z.string()),
});
export const promptTableSchema = z.object({
    reportId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    /**
     * Where these answers were asked for, once for the whole table.
     *
     * It used to sit on every row, back when a question carried its own market.
     * It does not: the brand this report watches does, so every row on one
     * report has the same pair — and a field repeated identically down a column
     * invites a reader to look for the row where it differs.
     */
    country: z.string(),
    language: z.string(),
    from: isoDate,
    to: isoDate,
    rows: z.array(promptTableRowSchema),
});
/**
 * What one engine's last capture of a question did for the subject, in the
 * range: named it first, named it, left it out — or no finding yet, because
 * it is still being read, could not be read, has not been answered, or the
 * capture failed. Those last four are never counted as "left out" (§1).
 */
export const latestStateSchema = z.enum([
    "first",
    "named",
    "out",
    "reading",
    "unreadable",
    "waiting",
    "failed",
]);
/**
 * Each question's answers day by day and its last capture on each engine:
 * the line and the dots beside every row of the Questions table. A route of
 * its own, so the table an assistant pages through stays the size it was.
 */
export const promptTrendSchema = z.object({
    reportId: z.string(),
    from: isoDate,
    to: isoDate,
    rows: z.array(z.object({
        promptId: z.string(),
        /** Only days with an answer. A day with none is absent, never zero. */
        days: z.array(z.object({
            day: isoDate,
            responses: z.number().int().min(0),
            naming: z.number().int().min(0),
        })),
        latest: z.array(z.object({
            engine: z.string(),
            day: isoDate,
            runId: z.string(),
            state: latestStateSchema,
        })),
    })),
});
/**
 * One question's captures, one per engine per day (the newest attempt), and
 * what each did for the subject: the grid on the question's page, and the
 * counts above it. A day the question was not asked has no row.
 */
export const promptHistorySchema = z.object({
    promptId: z.string(),
    from: isoDate,
    to: isoDate,
    rows: z.array(z.object({
        day: isoDate,
        engine: z.string(),
        runId: z.string(),
        state: latestStateSchema,
    })),
});
/**
 * One question, opened up: every brand's standing on it.
 *
 * The table row says what the subject did; this says who else was in the answer
 * and where the subject sits among them. Same three tone counts rather than a
 * net, for the same reason as above — "balanced" and "nobody read it" are
 * opposite states that a single 0 makes identical.
 *
 * `responses` repeats on every brand and is the *same* number for all of them:
 * coverage here means "of the answers to this question, how many named you". A
 * per-brand denominator would score every brand 100% and say nothing.
 */
export const promptBrandStandingSchema = z.object({
    brandId: z.string(),
    brandName: z.string(),
    responses: z.number().int().min(0),
    naming: z.number().int().min(0),
    mentions: z.number().int().min(0),
    citations: z.number().int().min(0),
    positive: z.number().int().min(0),
    neutral: z.number().int().min(0),
    negative: z.number().int().min(0),
});
/**
 * One brand's coverage on this question, day by day.
 *
 * `mentioning` travels with `responses`, as everywhere: a day when one of one
 * answer named you and a day when forty of forty did are both 100%, and they
 * are not the same day.
 *
 * A day with no captures is absent for every brand rather than zero-filled —
 * "we asked and nobody said the name" and "we did not ask" are different facts,
 * and a chart that draws them the same way invents a drop that never happened.
 */
export const promptTimelineSchema = z.object({
    brandId: z.string(),
    brandName: z.string(),
    isSubject: z.boolean(),
    days: z.array(z.object({
        day: isoDate,
        responses: z.number().int().min(0),
        mentioning: z.number().int().min(0),
    })),
});
export const promptDetailSchema = z.object({
    promptId: z.string(),
    text: z.string(),
    country: z.string(),
    from: isoDate,
    to: isoDate,
    /** Which of `brands` is the report's own, so the screen can mark it. */
    subjectBrandId: z.string(),
    brands: z.array(promptBrandStandingSchema),
    /** The same brands over time, for the chart. */
    timelines: z.array(promptTimelineSchema),
});
/**
 * One answer to one question, as the Responses tab lists it.
 *
 * The overall `label` is the verdict; `points` is what the verdict was built
 * from. Both come from the same model call and are stored or discarded
 * together, so an answer never carries a breakdown without a judgement or a
 * judgement without the claims behind it.
 *
 * `label`, `evidence` and `points` travel together and are empty together: a capture is a
 * scrape and a judgement is a model call, so an answer nobody has read yet is a
 * real state rather than a gap to fill with a zero.
 */
export const promptResponseSchema = z.object({
    runId: z.string(),
    engine: z.string(),
    runDate: isoDate,
    /** The opening of the answer. The rest is never sent to this screen. */
    answerText: z.string().nullable(),
    label: z.enum(["positive", "neutral", "negative"]).nullable(),
    /** The words the judgement rests on, verbatim, so a reader can disagree. */
    evidence: z.string().nullable(),
    /**
     * Each separate thing this answer said about the brand, with its own label.
     *
     * The bands a screen draws are counts of these — which is what a single
     * `label` cannot express. An answer that calls a brand the best choice and
     * then warns about its governance has one positive point and one negative
     * one, and the overall label is a verdict on which weighed more.
     */
    points: z.array(z.object({
        label: z.enum(["positive", "neutral", "negative"]),
        text: z.string(),
    })),
    /** Whether this answer named the subject at all. */
    mentioned: z.boolean(),
    /** Whether it linked to a page the subject owns. */
    domainCited: z.boolean(),
    competitors: z.array(z.object({ brandId: z.string(), brandName: z.string() })),
});
export const promptResponsesSchema = z.object({
    promptId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    rows: z.array(promptResponseSchema),
});
/**
 * One page one question's answers cited.
 *
 * Grouped by URL: the same page cited by five answers is one finding — "this is
 * what the engines reach for" — and five identical rows would bury it.
 *
 * There is no domain category. Calling a domain "Brand", "Education" or "News"
 * is a judgement this product does not make and holds no source for, and a
 * column of guesses is worse than a column that is not there.
 *
 * `title` is whatever the engine labelled the link and is often absent. Null
 * stays null: a title derived from the URL would put words on a page nobody
 * read.
 */
export const promptCitationSchema = z.object({
    url: z.string(),
    domain: z.string().nullable(),
    title: z.string().nullable(),
    citations: z.number().int().min(0),
    /** True when the cited domain is one the subject owns. */
    own: z.boolean(),
    /**
     * The brands named in the answers that cited this page.
     *
     * The column that makes the table worth reading: a page cited by answers that
     * name three rivals and never you is a page to go and get.
     */
    brands: z.array(z.object({ brandId: z.string(), brandName: z.string() })),
});
export const promptCitationsSchema = z.object({
    promptId: z.string(),
    brandId: z.string(),
    brandName: z.string(),
    /** Every citation in range, so a share is against the real denominator. */
    total: z.number().int().min(0),
    rows: z.array(promptCitationSchema),
});
//# sourceMappingURL=prompt-table.js.map